import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { reApi, errMsg } from "@/lib/residentApi";
import { btnGold, field } from "@/components/nds/windows/finance/parts";
import AreaFix from "@/components/nds/windows/checkin/AreaFix";

const MIN = 5;
const pending = {};
/** Submit waits on this so a note typed just before clicking Submit is saved first. */
export const flushAreaNotes = (id) => pending[id]?.() || Promise.resolve();

function DoneArea({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`re-ck-area-${a.key}`}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`re-ck-area-toggle-${a.key}`} className="w-full flex items-center gap-2 py-1.5 text-left">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300 shrink-0" /><span className="text-sm text-nex-silver">{a.label}</span>
        {open ? <ChevronDown className="h-3 w-3 text-nex-muted ml-auto" /> : <ChevronRight className="h-3 w-3 text-nex-muted ml-auto" />}
      </button>
      {open && <div className="pl-6 pb-2 text-xs text-nex-muted break-words">{a.text}{a.note ? ` · Note: ${a.note}` : ""}</div>}
    </li>
  );
}

function TodoArea({ a, rec, note, setNote, locked, onFixed, onBlur }) {
  const [fix, setFix] = useState(false);
  const covered = (note || "").trim().length >= MIN;
  return (
    <li className="py-2 space-y-1.5" data-testid={`re-ck-area-${a.key}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${covered ? "text-sky-300" : "text-yellow-300"}`} />
        <div className="min-w-0 flex-1"><div className="text-sm text-nex-silver">{a.label}{covered && <span className="ml-2 font-mono text-[9px] uppercase text-sky-300">noted</span>}</div><div className="text-xs text-nex-muted break-words">{a.text}</div></div>
        {!locked && a.key !== "questions" && <button type="button" onClick={() => setFix(!fix)} data-testid={`re-ck-area-fix-${a.key}`}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 border border-nex-gold/50 rounded-sm font-mono text-[9px] uppercase tracking-[0.14em] text-nex-gold"><Wrench className="h-3 w-3" />{fix ? "Close" : "Complete it"}</button>}
      </div>
      {fix && <div className="ml-6 p-3 border border-white/10 rounded-sm bg-black/20" data-testid={`re-ck-area-fixpanel-${a.key}`}>
        <AreaFix area={a.key} residentId={rec.resident_id} residentName={rec.resident_name} onChanged={onFixed} onClose={() => { setFix(false); onFixed(); }} /></div>}
      {locked ? <div className="text-xs text-nex-silver pl-6">Note: {a.note || "—"}</div>
        : <div className="pl-6"><input value={note || ""} onChange={(e) => setNote(e.target.value)} placeholder="Or note why not" onBlur={onBlur}
          data-testid={`re-ck-area-note-${a.key}`} className={`${field} block w-full box-border`} /></div>}
    </li>
  );
}

/** Everything this resident still needs, on the check-in: fix it right here, or note why not. Submit unlocks once all are covered. */
export default function CheckinAreas({ rec, reload }) {
  const [d, setD] = useState(null);
  const [notes, setNotes] = useState({});
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => reApi.get(`/checkins/${rec.id}/areas`).then((x) => {
    setD(x);
    setNotes((n) => ({ ...Object.fromEntries(x.areas.map((a) => [a.key, a.note || ""])), ...Object.fromEntries(Object.entries(n).filter(([, v]) => v)) }));
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [rec.id, rec.status, rec.answers?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const save = async (quiet) => {
    setBusy(true);
    try { const x = await reApi.patch(`/checkins/${rec.id}/area-notes`, { notes }); setD(x); if (!quiet || x.ready) toast.success(x.ready ? "All areas covered · ready to submit" : "Notes saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dirty = d.areas.some((a) => (notes[a.key] || "") !== (a.note || ""));
  pending[rec.id] = dirty ? () => reApi.patch(`/checkins/${rec.id}/area-notes`, { notes }) : null;
  const done = d.areas.filter((a) => a.ok);
  const todo = d.areas.filter((a) => !a.ok);
  const covered = done.length + todo.filter((a) => (notes[a.key] || "").trim().length >= MIN).length;
  return (
    <div data-testid="re-ck-areas" className="p-4 border border-nex-gold/30 rounded-sm space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-serif text-nex-silver">{d.locked ? "Areas at submit" : `Still to do for ${rec.resident_name}`}</span>
        <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${covered === d.areas.length ? "text-emerald-300" : "text-yellow-300"}`} data-testid="re-ck-areas-count">
          {covered}/{d.areas.length} covered{d.locked ? " · recorded at submit" : ""}</span>
        {!d.locked && <button type="button" onClick={load} data-testid="re-ck-areas-refresh" className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-nex-muted hover:text-nex-silver">Re-check</button>}
      </div>
      {!d.locked && <p className="text-[11px] text-nex-muted">Complete each item here, or write why it isn't complete. Notes save when you leave the box. Submit works once all {d.areas.length} are covered.</p>}
      {todo.length ? <ul className="divide-y divide-white/5" data-testid="re-ck-areas-todo">
        {todo.map((a) => <TodoArea key={a.key} a={a} rec={rec} locked={d.locked} note={notes[a.key]} setNote={(v) => setNotes((x) => ({ ...x, [a.key]: v }))} onFixed={load} onBlur={() => dirty && save(true)} />)}
      </ul> : <p className="text-sm text-emerald-300" data-testid="re-ck-areas-all-done">Everything is complete.</p>}
      {done.length > 0 && <div className="border-t border-white/10 pt-2">
        <button type="button" onClick={() => setShowDone(!showDone)} data-testid="re-ck-areas-done-toggle" className="flex items-center gap-2 text-xs text-emerald-300">
          {showDone ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}{done.length} area{done.length === 1 ? "" : "s"} complete</button>
        {showDone && <ul className="mt-1" data-testid="re-ck-areas-done">{done.map((a) => <DoneArea key={a.key} a={a} />)}</ul>}
      </div>}
      {!d.locked && todo.length > 0 && <div className="flex justify-end">
        <button type="button" disabled={busy || !dirty} onClick={() => save(false)} data-testid="re-ck-areas-save" className={btnGold}>{busy ? "Saving…" : "Save notes"}</button></div>}
    </div>
  );
}
